import React, { useMemo } from "react";
import { Box, Collapse, Paper } from "@mui/material";
import AdminDataGrid from "./AdminDataGrid";

function textFromNode(node, depth = 0) {
  if (node == null || typeof node === "boolean" || depth > 4) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map((value) => textFromNode(value, depth + 1)).filter(Boolean).join(" ");
  if (React.isValidElement(node)) {
    const ownTitle = node.props?.title;
    if (typeof ownTitle === "string" && ownTitle.trim()) return ownTitle.trim();
    return textFromNode(node.props?.children, depth + 1);
  }
  return "";
}

/**
 * Backward-compatible adapter for the legacy AdminMuiTable API.
 *
 * Every existing Admin table that uses this component now renders through the
 * shared DataGrid implementation. Keeping this adapter avoids a risky rewrite
 * of each tab while preserving row clicks and expanded detail content.
 */
export default function AdminMuiTable({
  columns = [],
  rows = [],
  empty = null,
  isLight = true,
  maxHeight,
  showHeader = true,
  sortBy = "",
  sortDir = "asc",
  onSort,
  variant = "joy",
  fitColumns = false,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const gridRows = useMemo(
    () => safeRows.map((row, rowIndex) => ({
      id: String(row.key ?? rowIndex),
      __adminRow: row,
      __cells: Array.isArray(row.cells) ? row.cells : [],
    })),
    [safeRows]
  );
  const gridColumns = useMemo(() => columns.map((column, index) => {
    const width = typeof column.width === "number" ? column.width : undefined;
    const percent = typeof column.width === "string" && column.width.includes("%")
      ? Number.parseFloat(column.width) / 100
      : 0;
    return {
      field: column.id || `column-${index}`,
      headerName: typeof column.label === "string" ? column.label : "",
      description: typeof column.label === "string" ? column.label : undefined,
      width,
      minWidth: column.minWidth || width || 90,
      flex: width ? undefined : percent || 1,
      sortable: Boolean(column.sortable && onSort),
      align: column.align || "left",
      headerAlign: column.align || "left",
      renderCell: (params) => params.row.__cells[index] ?? "—",
      valueGetter: (_value, row) => textFromNode(row.__cells[index]),
    };
  }), [columns, onSort]);
  const selectedDetail = safeRows.find((row) => row.selected && row.detail != null);
  const sortModel = sortBy ? [{ field: sortBy, sort: sortDir }] : [];

  if (!safeRows.length) return empty || null;

  return (
    <Box sx={{ minWidth: 0 }}>
      <AdminDataGrid
        rows={gridRows}
        columns={gridColumns}
        isLight={isLight}
        height={maxHeight || 520}
        getRowId={(row) => row.id}
        onRowClick={(params) => params.row.__adminRow?.onClick?.()}
        onCellKeyDown={(params, event) => {
          const original = params.row.__adminRow;
          if ((event.key === "Enter" || event.key === " ") && original?.onKeyDown) original.onKeyDown(event);
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          if (onSort && model[0]?.field) onSort(model[0].field);
        }}
        columnHeaderHeight={showHeader ? 44 : 0}
        hideFooter
        sx={{
          borderRadius: variant === "plain" ? 0 : "10px",
          "& .MuiDataGrid-columnHeaders": showHeader ? undefined : { display: "none" },
          ...(fitColumns ? { "& .MuiDataGrid-cell": { minWidth: 0 } } : null),
        }}
      />
      {selectedDetail ? (
        <Collapse in timeout="auto">
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              borderRadius: "10px",
              border: isLight ? "1px solid #e3e8ef" : "1px solid #1E3448",
              background: isLight ? "#f8fafc" : "rgba(255,255,255,0.03)",
            }}
          >
            <Box sx={{ px: 3, py: 1.5, fontSize: 11.5, color: isLight ? "#64748B" : "rgba(255,255,255,0.62)", lineHeight: 1.55 }}>
              {selectedDetail.detail}
            </Box>
          </Paper>
        </Collapse>
      ) : null}
    </Box>
  );
}
